import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Layout from "@/components/Layout";
import { api, initials } from "@/lib/api";
import { Building2, Save, Upload, Trash2, Plus, Layers, Briefcase, ShieldCheck, Eye, EyeOff, User as UserIcon, X, Pencil, KeyRound } from "lucide-react";
import { compressImage } from "@/lib/image";
import { toast } from "sonner";
import { RentaConfigCard } from "@/components/RentaConfigCard";
import { tablasRenta, validarTablaRenta } from "@/lib/renta";
import { ACTIVIDAD_JORNADAS_ESPECIALES } from "@/lib/payroll";
import { normalizeRole, ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_ASISTENTE } from "@/lib/roles";

const PROVINCIAS_CR = ["San José", "Alajuela", "Cartago", "Heredia", "Guanacaste", "Puntarenas", "Limón"];

const Field = ({ label, children }) => (
  <div>
    <label className="np-label">{label}</label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="np-card">
    <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-5">{title}</div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">{children}</div>
  </div>
);

export default function Empresa() {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const fileRef = React.useRef(null);

  useEffect(() => {
    api.get("/empresa").then(({ data }) => setData(data || {}));
  }, []);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Solo se permiten imágenes."); return; }
    if (file.size > 10 * 1024 * 1024) { alert("Máximo 10 MB."); return; }
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, { maxDim: 480, quality: 0.85 });
      set("logo", dataUrl);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (data.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES) {
      const tablas = tablasRenta(data);
      for (const y of Object.keys(tablas)) {
        const err = validarTablaRenta(tablas[y]);
        if (err) { toast.error(`Impuesto sobre la Renta ${y}: ${err}`); return; }
      }
    }
    setSaving(true);
    try {
      const { data: saved } = await api.put("/empresa", data);
      setData(saved);
      setSavedMsg("Cambios guardados");
      setTimeout(() => setSavedMsg(""), 2500);
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 fade-in-up gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <Building2 size={26} strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-1">Datos generales</div>
            <h1 className="font-display text-3xl font-semibold">Empresa</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-sm text-success" data-testid="empresa-saved">{savedMsg}</span>}
          <button onClick={submit} disabled={saving} className="np-btn np-btn-primary" data-testid="empresa-save-btn">
            <Save size={16} /> {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <div className="np-card">
          <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-5">Logo e Identidad</div>
          <div className="flex items-center gap-5 p-4 rounded-2xl bg-[#FAFAF7] border border-c">
            <div className="relative w-24 h-24 flex-shrink-0">
              {data.logo ? (
                <img src={data.logo} alt="" className="w-24 h-24 rounded-2xl object-contain bg-white ring-1 ring-[var(--border)] p-2" data-testid="empresa-logo-preview" />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center font-display text-2xl font-semibold">
                  {(data.razon_social || "N")[0]?.toUpperCase()}
                </div>
              )}
              {uploading && <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner" /></div>}
            </div>
            <div className="flex-1">
              <div className="font-display text-xl font-semibold">{data.razon_social || "Empresa"}</div>
              <div className="text-xs text-secondary-c mt-0.5">Subí el logo (PNG/JPG/WebP). Se redimensiona automáticamente a 480 px.</div>
              <div className="flex gap-2 mt-3">
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogoPick} className="hidden" data-testid="empresa-logo-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="np-btn np-btn-outline text-sm py-1.5 px-3" data-testid="empresa-logo-upload-btn">
                  <Upload size={14} /> {data.logo ? "Cambiar logo" : "Subir logo"}
                </button>
                {data.logo && (
                  <button type="button" onClick={() => set("logo", "")} className="np-btn np-btn-ghost text-sm py-1.5 px-3 text-error" data-testid="empresa-logo-remove-btn">
                    <Trash2 size={14} /> Quitar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <Section title="Identificación Legal">
          <Field label="Razón Social *"><input className="np-input" data-testid="razon-social" value={data.razon_social || ""} onChange={e => set("razon_social", e.target.value)} required /></Field>
          <Field label="Tipo de Empresa">
            <select className="np-input" value={data.tipo_empresa || "Empresa Individual"} onChange={e => set("tipo_empresa", e.target.value)}>
              <option>Empresa Individual</option><option>Sociedad Anónima</option><option>S.R.L.</option>
            </select>
          </Field>
          <Field label="Cédula Jurídica"><input className="np-input" value={data.cedula_juridica || ""} onChange={e => set("cedula_juridica", e.target.value)} /></Field>
          <Field label="Representante Legal"><input className="np-input" value={data.representante_legal || ""} onChange={e => set("representante_legal", e.target.value)} /></Field>
          <Field label="Cédula Representante"><input className="np-input" value={data.cedula_representante || ""} onChange={e => set("cedula_representante", e.target.value)} /></Field>
          <Field label="Código Patrono CCSS"><input className="np-input" value={data.codigo_patrono_ccss || ""} onChange={e => set("codigo_patrono_ccss", e.target.value)} /></Field>
          <div className="md:col-span-3">
            <label className="np-label">Actividad Económica</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="empresa-actividad-grupo">
              {[
                { key: "comercio_hoteleria", title: "Comercio, Hotelería", subtitle: "Pago Quincenal · Mensual", hint: "Valor del día = salario mensual ÷ 30" },
                { key: "servicios_industria", title: "Servicios, Industria, Agricultura, Ganadería", subtitle: "Pago Semanal", hint: "Semanal: salario ÷ 26 · Quincenal/Mensual: salario ÷ 30" },
                { key: "servicios_jornadas_especiales", title: "Servicios con Jornadas especiales", subtitle: "Pago Quincenal · Mensual", hint: "Salario ÷ 30 · En trabajadores con jornada especial activa, las horas extras solo cuentan al superar 48 h semanales (lunes a domingo)" },
              ].map(opt => {
                const checked = data.actividad_grupo === opt.key;
                return (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked ? "border-primary-c bg-[#E6EFEA]" : "border-c hover:bg-[#FAFAF7]"}`}
                    data-testid={`empresa-actividad-${opt.key}`}
                  >
                    <input
                      type="radio"
                      name="actividad_grupo"
                      value={opt.key}
                      checked={checked}
                      onChange={() => set("actividad_grupo", opt.key)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{opt.title}</div>
                      <div className="text-xs text-primary-c font-medium mt-0.5">{opt.subtitle}</div>
                      <div className="text-[11px] text-secondary-c mt-1">{opt.hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </Section>

        {data.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES && (
          <Section title="Impuesto sobre la Renta">
            <div className="md:col-span-3">
              <RentaConfigCard empresa={data} onChange={(t) => set("renta_tablas", t)} />
            </div>
          </Section>
        )}

        <Section title="Contacto y Notificaciones">
          <Field label="Correo electrónico"><input className="np-input" type="email" value={data.correo || ""} onChange={e => set("correo", e.target.value)} /></Field>
          <Field label="WhatsApp (notificaciones)"><input className="np-input" value={data.whatsapp || ""} onChange={e => set("whatsapp", e.target.value)} /></Field>
          <Field label="Teléfono principal"><input className="np-input" value={data.telefono || ""} onChange={e => set("telefono", e.target.value)} /></Field>
        </Section>

        <Section title="Ubicación">
          <Field label="Provincia">
            <select className="np-input" value={data.provincia || ""} onChange={e => set("provincia", e.target.value)}>
              <option value="">Seleccionar...</option>
              {PROVINCIAS_CR.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Cantón"><input className="np-input" value={data.canton || ""} onChange={e => set("canton", e.target.value)} /></Field>
          <Field label="Dirección"><input className="np-input" value={data.direccion || ""} onChange={e => set("direccion", e.target.value)} /></Field>
        </Section>

        <ListaDepartamentos data={data} set={set} />
        <ListaPuestos data={data} set={set} />
        <ListaPerfiles />

        <div className="np-card">
          <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-5">Configuración de Planilla</div>
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-4 rounded-xl border border-c cursor-pointer hover:bg-[#FAFAF7]">
              <input type="checkbox" checked={!!data.extras_dobles_feriado} onChange={e => set("extras_dobles_feriado", e.target.checked)} className="mt-0.5" data-testid="empresa-extras-dobles-feriado" />
              <div>
                <div className="font-medium text-sm">Pagar las horas extras dobles en días feriados</div>
                <div className="text-xs text-secondary-c mt-0.5">
                  Si está activo, las horas extras realizadas en un día feriado se pagan al doble (2 × hora extra normal).
                  Si no, el día ordinario se paga doble pero las extras se mantienen a su tarifa normal (1,5x).
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-4 rounded-xl border border-c cursor-pointer hover:bg-[#FAFAF7]">
              <input type="checkbox" checked={!!data.ccss_aplica_extras} onChange={e => set("ccss_aplica_extras", e.target.checked)} className="mt-0.5" data-testid="empresa-ccss-aplica-extras" />
              <div>
                <div className="font-medium text-sm">Aplicar rebajo CCSS también sobre las horas extras</div>
                <div className="text-xs text-secondary-c mt-0.5">
                  Si está activo, el 10,83 % de CCSS se descuenta sobre el salario ordinario + extras.
                  Si no está marcado, solo se descuenta sobre el salario base (horas ordinarias).
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-4 rounded-xl border border-c cursor-pointer hover:bg-[#FAFAF7]">
              <input type="checkbox" checked={!!data.reloj_marcador_csv_enabled} onChange={e => set("reloj_marcador_csv_enabled", e.target.checked)} className="mt-0.5" data-testid="empresa-reloj-marcador-csv" />
              <div>
                <div className="font-medium text-sm">Habilitar carga de CSV de reloj marcador en Asistencia</div>
                <div className="text-xs text-secondary-c mt-0.5">
                  Si está activo, en la pantalla de Asistencia aparecerá un botón <strong>Adjuntar CSV</strong> para importar las horas de entrada y salida desde un archivo exportado por el reloj marcador (acepta .csv y .xlsx).
                  El sistema empareja a cada trabajador por cédula (y por nombre como respaldo) y actualiza su registro para la fecha correspondiente.
                </div>
              </div>
            </label>
            <div className="p-4 rounded-xl border border-c bg-[#FAFAF7]">
              <div className="font-medium text-sm">Tasa CCSS para empleados pensionados</div>
              <div className="text-xs text-secondary-c mt-0.5 mb-2">
                Los pensionados del IVM que continúan trabajando cotizan únicamente SEM (Seguro de Enfermedad y Maternidad). La tasa estándar es <strong>5,50 %</strong>.
                Ajustala si tu régimen aplica una tasa diferente (ej: 6,50 % si incluís BP).
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  className="np-input max-w-[130px]"
                  value={data.ccss_pensionado_pct ?? 5.50}
                  onChange={e => set("ccss_pensionado_pct", parseFloat(e.target.value) || 0)}
                  data-testid="empresa-ccss-pensionado-pct"
                />
                <span className="text-sm text-secondary-c">%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="np-card">
          <label className="np-label">Notas adicionales</label>
          <textarea className="np-input" rows={4} value={data.notas || ""} onChange={e => set("notas", e.target.value)} placeholder="Información adicional relevante..." />
        </div>
      </form>
    </Layout>
  );
}

// ==================== Departamentos ====================
function ListaDepartamentos({ data, set }) {
  const lista = Array.isArray(data.departamentos) ? data.departamentos : [];
  const [nuevo, setNuevo] = useState("");

  const agregar = () => {
    const nombre = nuevo.trim();
    if (!nombre) return;
    if (lista.some(d => d.toLowerCase() === nombre.toLowerCase())) return;
    set("departamentos", [...lista, nombre]);
    setNuevo("");
  };

  const eliminar = (idx) => {
    const cp = [...lista]; cp.splice(idx, 1);
    set("departamentos", cp);
  };

  return (
    <div className="np-card">
      <div className="flex items-center gap-2 mb-4">
        <Layers size={16} className="text-primary-color" />
        <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Departamentos</div>
      </div>
      <p className="text-xs text-secondary-c mb-4">Definí las áreas o departamentos de la empresa. Estarán disponibles como opciones al asignar empleados.</p>

      <div className="flex gap-2 mb-4">
        <input
          className="np-input flex-1"
          placeholder="Ej: Administración, Ventas, Producción..."
          value={nuevo}
          onChange={e => setNuevo(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); agregar(); } }}
          data-testid="empresa-dept-input"
        />
        <button type="button" onClick={agregar} className="np-btn np-btn-primary" data-testid="empresa-dept-add-btn">
          <Plus size={14} /> Agregar
        </button>
      </div>

      {lista.length === 0 ? (
        <div className="text-sm text-secondary-c italic py-3 text-center rounded-xl border border-dashed border-c">
          Aún no hay departamentos definidos.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="empresa-dept-list">
          {lista.map((d, i) => (
            <span key={`dept-${d}`} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6EFEA] text-primary-color text-sm font-medium" data-testid={`empresa-dept-item-${i}`}>
              {d}
              <button type="button" onClick={() => eliminar(i)} className="hover:text-error" title="Eliminar" data-testid={`empresa-dept-remove-${i}`}>
                <Trash2 size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== Puestos ====================
function ListaPuestos({ data, set }) {
  const lista = Array.isArray(data.puestos) ? data.puestos : [];
  const [nuevo, setNuevo] = useState("");
  const [noExtras, setNoExtras] = useState(false);

  const agregar = () => {
    const nombre = nuevo.trim();
    if (!nombre) return;
    if (lista.some(p => (p.nombre || "").toLowerCase() === nombre.toLowerCase())) return;
    set("puestos", [...lista, { nombre, no_horas_extras: !!noExtras }]);
    setNuevo("");
    setNoExtras(false);
  };

  const eliminar = (idx) => {
    const cp = [...lista]; cp.splice(idx, 1);
    set("puestos", cp);
  };

  const toggleNoExtras = (idx) => {
    const cp = lista.map((p, i) => i === idx ? { ...p, no_horas_extras: !p.no_horas_extras } : p);
    set("puestos", cp);
  };

  return (
    <div className="np-card">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase size={16} className="text-primary-color" />
        <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Puestos</div>
      </div>
      <p className="text-xs text-secondary-c mb-4">
        Definí los puestos de trabajo. Marcá <strong>&quot;No pagar horas extras&quot;</strong> para puestos de confianza o jefatura (Art. 143 CT).
        Cuando calculés la planilla, las horas extras de esos empleados se omitirán automáticamente.
      </p>

      <div className="rounded-xl border border-c p-3 space-y-3 mb-4 bg-[#FAFAF7]">
        <div className="flex gap-2 flex-wrap">
          <input
            className="np-input flex-1 min-w-[200px]"
            placeholder="Ej: Gerente, Contador, Vendedor..."
            value={nuevo}
            onChange={e => setNuevo(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); agregar(); } }}
            data-testid="empresa-puesto-input"
          />
          <button type="button" onClick={agregar} className="np-btn np-btn-primary" data-testid="empresa-puesto-add-btn">
            <Plus size={14} /> Agregar
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={noExtras}
            onChange={e => setNoExtras(e.target.checked)}
            data-testid="empresa-puesto-new-noextras"
          />
          <span>Este puesto <strong>no paga horas extras</strong> (jefatura / confianza)</span>
        </label>
      </div>

      {lista.length === 0 ? (
        <div className="text-sm text-secondary-c italic py-3 text-center rounded-xl border border-dashed border-c">
          Aún no hay puestos definidos.
        </div>
      ) : (
        <div className="space-y-2" data-testid="empresa-puesto-list">
          {lista.map((p, i) => (
            <div key={`puesto-${p.nombre}`} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-c bg-white" data-testid={`empresa-puesto-item-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{p.nombre}</div>
                {p.no_horas_extras && (
                  <div className="text-xs text-warning font-medium mt-0.5">No paga horas extras</div>
                )}
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!p.no_horas_extras}
                  onChange={() => toggleNoExtras(i)}
                  data-testid={`empresa-puesto-toggle-noextras-${i}`}
                />
                <span>Sin horas extras</span>
              </label>
              <button type="button" onClick={() => eliminar(i)} className="np-btn np-btn-ghost p-2 text-error" title="Eliminar" data-testid={`empresa-puesto-remove-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== Perfiles (Usuarios del sistema) ====================
const ROLE_LABEL = { [ROLE_ADMIN]: "Administrador", [ROLE_SUPER_ADMIN]: "Super Admin", [ROLE_ASISTENTE]: "Asistente" };
const ROLE_DESCRIPTION = {
  [ROLE_ADMIN]: "Acceso total al sistema (todos los módulos, empresa, empleados, planillas, etc.).",
  [ROLE_ASISTENTE]: "Solo Asistencia, Incapacidades, Rebajos, Bonificaciones y Solicitudes. El resto permanece oculto.",
};

function ListaPerfiles() {
  const [perfiles, setPerfiles] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'new' | 'edit', perfil? }

  const load = async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([api.get("/perfiles"), api.get("/empleados")]);
      setPerfiles(p.data || []);
      setEmpleados(e.data || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error("Solo administradores pueden ver los perfiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const eliminar = async (perfil) => {
    if (!window.confirm(`¿Eliminar el perfil de ${perfil.name || perfil.email}? El usuario perderá el acceso al sistema.`)) return;
    try {
      await api.delete(`/perfiles/${perfil.user_id}`);
      toast.success("Perfil eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar el perfil");
    }
  };

  const empleadoDe = (empleado_id) => empleados.find(e => e.id === empleado_id);

  return (
    <div className="np-card" data-testid="empresa-perfiles-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-primary-color" />
          <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Perfiles</div>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: "new" })}
          className="np-btn np-btn-primary text-sm py-1.5 px-3"
          data-testid="perfil-new-btn"
        >
          <Plus size={14} /> Nuevo perfil
        </button>
      </div>
      <p className="text-xs text-secondary-c mb-4">
        Asigná credenciales de acceso al sistema a un trabajador específico. Elegí un <strong>rol</strong> que determina qué secciones podrá ver:
        <span className="block mt-1">• <strong>Administrador</strong>: acceso total.</span>
        <span className="block">• <strong>Asistente</strong>: solo Asistencia, Incapacidades, Rebajos, Bonificaciones y Solicitudes.</span>
      </p>

      {loading ? (
        <div className="text-sm text-secondary-c py-6 text-center">Cargando perfiles…</div>
      ) : perfiles.length === 0 ? (
        <div className="text-sm text-secondary-c italic py-6 text-center rounded-xl border border-dashed border-c">
          Aún no hay perfiles creados.
        </div>
      ) : (
        <div className="space-y-2" data-testid="empresa-perfiles-list">
          {perfiles.map(p => {
            const role = normalizeRole(p.role);
            const emp = empleadoDe(p.empleado_id);
            const displayName = p.name || emp?.nombre || p.email;
            const isGoogle = p.auth_provider === "google";
            return (
              <div key={p.user_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-c bg-white" data-testid={`perfil-row-${p.user_id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {emp?.avatar ? (
                    <img src={emp.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {initials(displayName)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{displayName}</span>
                      <span className={`np-badge text-[10px] ${role === ROLE_ADMIN || role === ROLE_SUPER_ADMIN ? "np-badge-success" : "np-badge-info"}`}>
                        {ROLE_LABEL[role] || role}
                      </span>
                      {isGoogle && <span className="np-badge text-[10px] np-badge-neutral">Google</span>}
                    </div>
                    <div className="text-xs text-secondary-c mt-0.5 truncate">
                      {p.email}
                      {emp ? <> · Trabajador: <strong className="text-primary-c">{emp.nombre} {emp.apellidos}</strong></> : null}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", perfil: p })}
                    className="np-btn np-btn-ghost p-2"
                    title="Editar"
                    data-testid={`perfil-edit-${p.user_id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => eliminar(p)}
                    className="np-btn np-btn-ghost p-2 text-error hover:bg-[#FDEDED]"
                    title="Eliminar"
                    data-testid={`perfil-delete-${p.user_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <PerfilModal
          mode={modal.mode}
          perfil={modal.perfil}
          empleados={empleados}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function PerfilModal({ mode, perfil, empleados, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [data, setData] = useState(() => ({
    email: perfil?.email || "",
    password: "",
    role: normalizeRole(perfil?.role) || ROLE_ASISTENTE,
    empleado_id: perfil?.empleado_id || "",
    name: perfil?.name || "",
  }));
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Multi-tenant: si el email ya existe en otra empresa, el perfil se crea
  // vinculado y hereda la contraseña actual del usuario. `linkedInfo` guarda
  // esa detección para adaptar el formulario en tiempo real.
  const [linkedInfo, setLinkedInfo] = useState(null); // { exists, in_same_empresa, name }

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  // Al escribir el email (creación), consultamos si ya existe.
  useEffect(() => {
    if (isEdit) return;
    const email = String(data.email || "").trim().toLowerCase();
    if (!email || !email.includes("@") || !email.includes(".")) {
      setLinkedInfo(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/perfiles/check-email?email=${encodeURIComponent(email)}`);
        if (!cancelled) setLinkedInfo(r.data || null);
      } catch {
        if (!cancelled) setLinkedInfo(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [data.email, isEdit]);

  // Si es perfil vinculado (email existente en otra empresa), no requerimos password
  const isLinkedProfile = !isEdit && !!(linkedInfo?.exists && !linkedInfo?.in_same_empresa);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError("");
    setSaving(true);
    try {
      // Si el usuario eligió un empleado y no tocó el nombre, autocompletamos
      let payload = { ...data };
      if (payload.empleado_id && !payload.name) {
        const emp = empleados.find(x => x.id === payload.empleado_id);
        if (emp) payload.name = `${emp.nombre || ""} ${emp.apellidos || ""}`.trim();
      }
      if (isEdit) {
        // En edición, la contraseña es opcional
        if (!payload.password) delete payload.password;
        await api.put(`/perfiles/${perfil.user_id}`, payload);
        toast.success("Perfil actualizado");
      } else {
        // Perfil vinculado: no enviamos password (el backend la hereda).
        if (isLinkedProfile) delete payload.password;
        await api.post("/perfiles", payload);
        toast.success(isLinkedProfile
          ? "Perfil vinculado creado — el usuario podrá elegir esta empresa al iniciar sesión con su contraseña actual"
          : "Perfil creado — el trabajador ya puede iniciar sesión");
      }
      onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo guardar el perfil";
      setError(typeof msg === "string" ? msg : "Error de validación");
    } finally {
      setSaving(false);
    }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl fade-in-up" onClick={(e) => e.stopPropagation()} data-testid="perfil-modal">
        <div className="p-5 border-b border-c flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary-color" />
            <h3 className="font-display text-xl font-semibold">{isEdit ? "Editar perfil" : "Nuevo perfil"}</h3>
          </div>
          <button onClick={onClose} className="np-btn np-btn-ghost p-2" data-testid="perfil-modal-close"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="np-label">Trabajador</label>
            <select
              className="np-input"
              value={data.empleado_id}
              onChange={e => {
                const empId = e.target.value;
                set("empleado_id", empId);
                const emp = empleados.find(x => x.id === empId);
                if (emp) {
                  set("name", `${emp.nombre || ""} ${emp.apellidos || ""}`.trim());
                  if (!isEdit && !data.email && emp.correo) set("email", emp.correo);
                }
              }}
              data-testid="perfil-empleado"
            >
              <option value="">— Sin trabajador asignado —</option>
              {empleados.map(e => (
                <option key={e.id} value={e.id}>{e.nombre} {e.apellidos} · {e.puesto || "—"}</option>
              ))}
            </select>
            <div className="text-[11px] text-secondary-c mt-1">Podés elegir a qué trabajador pertenece este perfil (opcional).</div>
          </div>

          <div>
            <label className="np-label">Nombre a mostrar</label>
            <input
              className="np-input"
              value={data.name}
              onChange={e => set("name", e.target.value)}
              placeholder="Ej: María Pérez"
              data-testid="perfil-name"
            />
          </div>

          <div>
            <label className="np-label">Correo electrónico *</label>
            <input
              type="email"
              className="np-input"
              required
              value={data.email}
              onChange={e => set("email", e.target.value)}
              data-testid="perfil-email"
              autoComplete="off"
              disabled={isEdit}
            />
            {isLinkedProfile && (
              <div className="mt-2 p-2.5 rounded-lg bg-[#E6EFEA] border border-[#B8D3BF] text-xs text-primary-c flex items-start gap-2" data-testid="perfil-linked-notice">
                <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{linkedInfo?.name || "Este usuario"}</strong> ya tiene una cuenta en otra empresa. Se creará un <strong>perfil vinculado</strong> que compartirá la contraseña actual del usuario. Podrá elegir esta empresa al iniciar sesión.
                </span>
              </div>
            )}
            {linkedInfo?.exists && linkedInfo?.in_same_empresa && !isEdit && (
              <div className="mt-2 p-2.5 rounded-lg bg-[#FEEBE8] border border-[#F5C6CB] text-xs text-error" data-testid="perfil-dup-notice">
                Ya existe un perfil con este correo en esta empresa. Editá el perfil existente en la lista.
              </div>
            )}
          </div>

          {!isLinkedProfile && (
            <div>
              <label className="np-label">
                Contraseña {isEdit ? "(dejar vacío para no cambiarla)" : "*"}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  className="np-input pr-10"
                  required={!isEdit}
                  minLength={6}
                  value={data.password}
                  onChange={e => set("password", e.target.value)}
                  placeholder={isEdit ? "••••••" : "Mínimo 6 caracteres"}
                  data-testid="perfil-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-c hover:text-primary-c p-1"
                  tabIndex={-1}
                  data-testid="perfil-password-toggle"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="np-label">Rol *</label>
            <div className="space-y-2">
              {[ROLE_ADMIN, ROLE_ASISTENTE].map(r => (
                <label
                  key={r}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${data.role === r ? "border-primary-c bg-[#E6EFEA]" : "border-c hover:bg-[#FAFAF7]"}`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r}
                    checked={data.role === r}
                    onChange={() => set("role", r)}
                    className="mt-0.5"
                    data-testid={`perfil-role-${r}`}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {r === ROLE_ADMIN ? <ShieldCheck size={14} /> : <UserIcon size={14} />}
                      {ROLE_LABEL[r]}
                    </div>
                    <div className="text-xs text-secondary-c mt-0.5">{ROLE_DESCRIPTION[r]}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-[#FDEDED] border border-[#F0C6C6] text-sm text-error" data-testid="perfil-modal-error">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-c">
            <button type="button" onClick={onClose} className="np-btn np-btn-ghost" data-testid="perfil-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="np-btn np-btn-primary" data-testid="perfil-modal-save">
              <KeyRound size={14} /> {saving ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear perfil")}
            </button>
          </div>
        </form>
      </div>
    </div>
  ), document.body);
}
